import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";
import { AppSettings } from "../../../../../shared/app.settings";
import { CommonBindingDataService } from "../../../../../shared/services/common-binding-data.service";
import { MenuItem, MessageService } from "primeng/api";
import { EntityService } from "../../../../../shared/services/entity.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { Country } from "../../../../../shared/models/country";
import { Router } from "@angular/router";
import { MiFormComponent } from "app/modules/shared/components/mi-form/mi-form.component";
import { AttributeData } from "app/modules/shared/models/attribute.models";
import { EntityResponse } from "app/modules/shared/models/entityResponse.models";
import { Language } from "app/modules/shared/models/language";
import * as _ from 'lodash';

interface UserAttributeData {
  tabs: Array<{
    tabCode: string;
    tabIndex: number;
    groups: Array<{
      fields: Array<{
        attributeCode: string;
        attributeValue: string;
        isDisable: boolean;
        disabled?: boolean;
        validation?: { required: boolean };
        presetValues: Array<{
          labelValue: string;
          labelKey: string;
        }>;
      }>;
    }>;
  }>;
  [key: string]: unknown;
}

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  [key: string]: unknown;
}

interface RelationData {
  entityRelationshipConfigId: string;
  otherEntityCode: string;
  relation: string;
  [key: string]: unknown;
}

interface AttributeValues {
  user_parent?: string;
  protocol_user_type?: string;
  [key: string]: unknown;
}

interface SponsorDetails {
  [key: string]: unknown;
}

interface EntityObjEvent {
  type: string;
  id: string;
  [key: string]: unknown;
}

interface UserData {
  forTenantCode: string;
  entityCode: string;
  countryCode: string;
  languageCode: string;
  data: Array<{
    attributeCode: string;
    attributeValue: string;
  }>;
  relationshipData: Array<{
    entityRelationshipConfigId: string;
    otherEntityId: string;
  }>;
}

interface EntitiesData {
  countryCode: string;
  tenantCode: string;
}

@Component({
  selector: 'app-add-protocol-user',
  standalone: true,
  imports: [
    BreadcrumbModule,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    FormsModule,
    MiFormComponent
  ],
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss', '../../../../../shared/components/mi-fields/mobile-input/flags.scss']
})
export class AddProtocolUserComponent implements OnInit {
  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Input() userId: string;
  @Output() closePopup = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();
  @Input() sponsorDetails: SponsorDetails;
  @Input() protocolId: string;
  @Input() isChangeReasonNotRequired: boolean = false;
  activeIndex: number = 0;
  data: AttributeData;
  userAttributeData: UserAttributeData;
  routePath: MenuItem[] = [];
  attributeLabels: Record<string, string> = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: ColumnDefinition[] = [];
  userAttributeLength: number;
  userIdForDocument: string;
  userDocumentId: string;
  driverRelation: RelationData;
  attributeValues: AttributeValues = {};
  previousBtnLabel: string;
  label: string;
  saveUserBtnLabel: string;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode: string;
  tabIndex: number;
  loadDialogView = false;
  userHeader: string;
  originalUserAttributeData: UserAttributeData;
  protocolUserType: string;

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private configService: ConfigService
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeUserService();
    this.loadUserData();
    this.activeIndex = 0;
    this.cs.entityObj$.subscribe((_event: EntityObjEvent) => {
      this.protocolUserType = _event.type;
    });
  }

  private initializeLabels() {
    this.saveUserBtnLabel = this.userId ? this.cs.getLabel("protocol.lbl_update_protocol_user") :
      this.cs.getLabel("protocol.lbl_add_protocol_user");
    this.userHeader = this.userId ? this.cs.getLabel("protocol.lbl_update_protocol_user") :
      this.cs.getLabel("protocol.lbl_add_protocol_user");
  }

  private initializeUserService() {
    this.moduleName = AppSettings.ENTITY_TYPE.PROTOCOL_USER;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
  }

  private loadUserData() {
    this.entityService.setRelatedData(this.sponsorDetails);
    if (this.userId) {
      this.entityService
        .getEntity(this.userId, AppSettings.ENTITY_CODE.PROTOCOL_USER, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe((res: EntityResponse) => {
          if (res) {
            this.processUserResponse(res);
            this.isNew = false;
          }
        });
    } else {
      this.getAttributes();
    }
  }

  private processUserResponse(data: EntityResponse) {
    this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as AttributeValues;
    const entityObj = { type: this.attributeValues.protocol_user_type, id: this.attributeValues.user_parent }
    this.cs.emitEntityObj(entityObj);
    this.getAttributes();
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_TYPE.PROTOCOL_USER;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;
    this.entityService.getAttributeDefinition(entityType, viewCode).subscribe((res: unknown) => {
      if (!res) return;
      this.setUserView(res);
    });
  }

  setUserView(res: unknown) {
    this.loadDialogView = true;
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.originalUserAttributeData = this.cs.getOrganizedAttribute(res) as unknown as UserAttributeData;
    this.userAttributeData = _.cloneDeep(this.originalUserAttributeData);
    this.userAttributeData.tabs = this.userAttributeData?.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.map(field => field)
          .filter((field: { attributeCode: string;[key: string]: unknown }) => {
            if (field.attributeCode === 'protocol_entity_id') {
              return false;
            }
            if (field.attributeCode === 'user_parent') {
              return false;
            }
            if (field.attributeCode === 'user_role' || field.attributeCode === 'user_id' || field.attributeCode === 'protocol_user_type') {
              if (this.userId) {
                field.disabled = true;
              }
            }
            if (field.attributeCode === 'change_reason') {
              if (this.isChangeReasonNotRequired) {
                return false;
              } else {
                if (field.validation) {
                  (field.validation as { required: boolean }).required = true;
                }
                return true;
              }
            }
            return true;
          }
          )
      }))
    }));
    this.userAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.userAttributeData?.tabs);
    this.userAttributeLength = this.userAttributeData.tabs.length;
    this.driverRelation = _.find(res as { relations: RelationData[] }, { otherEntityCode: 'driver', relation: "oneToMany" });
  }

  onSaveUser(event: Record<string, unknown>) {
    const userData: UserData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: [],
      relationshipData: []
    };

    const uData = event;
    for (const [key, value] of Object.entries(uData)) {
      const attributeValue = value as string;
      if (key === 'protocol_user_type') {
        userData.data.push({
          attributeCode: 'user_parent',
          attributeValue
        })
      } else {
        userData.data.push({
          attributeCode: key,
          attributeValue,
        });
      }
    }
    userData.data.push({
      attributeCode: 'protocol_entity_id',
      attributeValue: this.protocolId
    })
    userData.data.push({
      attributeCode: 'protocol_user_type',
      attributeValue: this.protocolUserType
    })

    const entitiesData: EntitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };
    if (!this.userId) {
      this.entityService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: EntityResponse) => {
          this.userId = res.entityId;
          this.saveUserData(userData, true);
        });

    } else {
      this.saveUserData(userData, this.isNew);
    }
  }

  saveUserData(userData: UserData, isNew: boolean) {
    userData.data = this.cs.mapAttributeIds(userData.data, this.originalUserAttributeData.tabs)
    this.entityService
      .saveAttributeData(userData.entityCode, this.userId, userData).subscribe({
        next: () => {
          if (isNew) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('protocol_user.lbl_protocol_user_added') });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('protocol_user.lbl_protocol_user_updated') });
          }
          this.saveSuccess.emit();
          this.closeDialog(true);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'manage_user', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'manage_user', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  closeDialog(data) {
    this.closePopup.emit(data);
  }
}
